import { createAsyncThunk } from '@reduxjs/toolkit';
import loadStructure from './loadStructure';
import { TApiaFinderFilter, TAsyncThunkState } from '../../../types';
import store from '../../../..';
import { responsiveTableActions } from '../../../../responsiveTableSlice';
import { getCurrentListId } from '../../../../../components/apia/common/lists/hooks/useCurrentListId';
import updateData from './updateData';
import { notify } from '../../../../../components/common/notifications/util';
import { formatMessage } from '../../../../../utils/util';
import ApiaApi, { makeApiaUrl } from '../../../../../api/apiaApi/apiaApi';
import { TApiaFilter } from '../../../../../types';
import queriesQueue, { TQueriesActionsChain } from '../../../queueController';
import dayjs from 'dayjs';
import { getDateFormat } from '@apia/util';

type TSearch = {
  fieldId: string;
  changingFilters?: TApiaFinderFilter[];
  query?:
    | {
        workingMode: string;
      }
    // eslint-disable-next-line @typescript-eslint/ban-types
    | {};
  forceSearch?: boolean;
  avoidDetectOnChangeAndSearch?: boolean;
  forceRefreshFromSavedFilters?: boolean;
  queueKey?: TQueriesActionsChain;
};
export const validateDateFilters = (filters: TApiaFinderFilter[]): boolean => {
  const dateFilters = filters.filter(
    (f) => f.apiaFilter.type === 'D' || f.apiaFilter.type === 'date',
  );
  const dateFormat = getDateFormat();
  let dateFiltersValidated = 0;
  dateFilters.forEach((df) => {
    const filter = df.apiaFilter;
    const initialDate = filter.currentValue;
    const finalDate = filter.filterToValue;
    if (filter.hideToFilter) {
      // Single-date filter
      const isValid =
        initialDate === '' || dayjs(initialDate, dateFormat).isValid();
      if (isValid) dateFiltersValidated++;
    } else {
      // Range-date filter
      const startValid =
        initialDate === '' || dayjs(initialDate, dateFormat).isValid();
      const endValid =
        finalDate === '' || dayjs(finalDate, dateFormat).isValid();

      if (!startValid || !endValid) {
        return;
      }

      if (initialDate !== '' && finalDate !== '') {
        // If both have values, check that end >= start
        const start = dayjs(initialDate, dateFormat);
        const end = dayjs(finalDate, dateFormat);
        if (end.isAfter(start) || end.isSame(start, 'day')) {
          dateFiltersValidated++;
        }
      } else {
        // Only one date given, that's still valid
        dateFiltersValidated++;
      }
    }
  });
  return dateFilters.length === dateFiltersValidated;
};
const search = createAsyncThunk<
  null | { filters: TApiaFinderFilter[] } | { filtersApplied: boolean },
  TSearch,
  TAsyncThunkState
>(
  'querySlice/searcher',
  async (
    {
      fieldId,
      query,
      changingFilters,
      forceSearch,
      avoidDetectOnChangeAndSearch,
      forceRefreshFromSavedFilters,
      queueKey = 'directSearch',
    },
    thunkApi,
  ) => {
    store.dispatch(
      responsiveTableActions.update({
        tableName: getCurrentListId(),
        selectedRows: [],
      }),
    );
    const state = thunkApi.getState().querySlice.queries[fieldId];

    // Helper function to check if a value is empty (undefined or empty string)
    const isEmpty = (value: any) => value === undefined || value === '';

    // Helper function to add or remove error based on condition
    const setError = (
      filter: TApiaFinderFilter,
      errorField: keyof TApiaFilter,
      condition: boolean,
      errorMessage: string = '',
    ): TApiaFinderFilter => {
      return {
        ...filter,
        apiaFilter: {
          ...filter.apiaFilter,
          [errorField]: condition ? errorMessage : undefined,
        },
      };
    };
    let isRequirementMet = false;
    let required = 0;
    let withValue = 0;
    const emptyFilters: string[] = [];
    // La siguiente logica se encarga de contar cuantos filtros requeridos hay, y luego cuantos tienen valor
    state.filters?.forEach((innerFilter) => {
      if (innerFilter.apiaFilter.required) {
        required++;

        if (['date', 'D'].includes(innerFilter.apiaFilter.type as string)) {
          const { apiaFilter } = innerFilter;
          if (
            apiaFilter.dateFromRequired &&
            !isEmpty(apiaFilter.currentValue)
          ) {
            withValue++;
          } else if (
            apiaFilter.dateRequired &&
            !isEmpty(apiaFilter.currentValue) &&
            !isEmpty(apiaFilter.filterToValue)
          ) {
            withValue++;
          } else if (
            apiaFilter.dateToRequired &&
            !isEmpty(apiaFilter.filterToValue)
          ) {
            withValue++;
          } else {
            emptyFilters.push(
              (innerFilter.apiaFilter.title ??
                innerFilter.apiaFilter.id) as string,
            );
          }
        } else if (innerFilter.apiaFilter.currentValue) {
          withValue++;
        } else {
          emptyFilters.push(
            (innerFilter.apiaFilter.title ??
              innerFilter.apiaFilter.id) as string,
          );
        }
      }
    });

    if (required === withValue) isRequirementMet = true;

    if (!state.hasSearched && state.modalData?.noExecFirstTime) {
      isRequirementMet = true;
    }

    /**
     * Esto se realiza porque los filtros del state no estan actualizados al momento de retornarlos al reducer (search.fullfiled).
     * Por lo que se obtienen del resultado de las acciones realizadas previamente y se parsean para ser retornados
     * @param stateFilters
     * @returns
     */
    const parseReturnFilters = (
      stateFilters: TApiaFinderFilter[] | undefined,
    ) => {
      const returnFilters: TApiaFinderFilter[] = [];

      stateFilters?.forEach((innerFilter) => {
        if (['date', 'D'].includes(innerFilter.apiaFilter.type as string)) {
          const { apiaFilter } = innerFilter;

          if (apiaFilter.required) {
            if (
              apiaFilter.dateFromRequired &&
              isEmpty(apiaFilter.currentValue)
            ) {
              returnFilters.push(
                setError(innerFilter, 'errorFrom', true, 'Filtro requerido'),
              );
            } else if (
              apiaFilter.dateRequired &&
              (isEmpty(apiaFilter.currentValue) ||
                isEmpty(apiaFilter.filterToValue))
            ) {
              if (
                isEmpty(apiaFilter.currentValue) &&
                isEmpty(apiaFilter.filterToValue)
              ) {
                returnFilters.push(
                  setError(innerFilter, 'error', true, 'Filtro requerido'),
                );
              } else if (
                isEmpty(apiaFilter.currentValue) &&
                !isEmpty(apiaFilter.filterToValue)
              ) {
                returnFilters.push(
                  setError(innerFilter, 'errorFrom', true, 'Filtro requerido'),
                );
              } else {
                returnFilters.push(
                  setError(innerFilter, 'errorTo', true, 'Filtro requerido'),
                );
              }
            } else if (
              apiaFilter.dateToRequired &&
              isEmpty(apiaFilter.filterToValue)
            ) {
              returnFilters.push(
                setError(innerFilter, 'errorTo', true, 'Filtro requerido'),
              );
            } else if (
              apiaFilter.dateFromRequired &&
              !isEmpty(apiaFilter.currentValue)
            ) {
              returnFilters.push(setError(innerFilter, 'errorFrom', false));
            } else if (
              apiaFilter.dateRequired &&
              !isEmpty(apiaFilter.currentValue) &&
              !isEmpty(apiaFilter.filterToValue)
            ) {
              returnFilters.push(setError(innerFilter, 'error', false));
            } else if (
              apiaFilter.dateToRequired &&
              !isEmpty(apiaFilter.filterToValue)
            ) {
              returnFilters.push(setError(innerFilter, 'errorTo', false));
            } else {
              returnFilters.push(innerFilter);
            }
          } else {
            returnFilters.push(innerFilter);
          }
        } else {
          const { apiaFilter } = innerFilter;

          if (apiaFilter.required) {
            if (isEmpty(apiaFilter.currentValue)) {
              returnFilters.push(
                setError(innerFilter, 'error', true, 'Filtro requerido'),
              );
            } else {
              returnFilters.push(setError(innerFilter, 'error', false));
            }
          } else {
            returnFilters.push(innerFilter);
          }
        }
      });
      return returnFilters;
    };
    const areDatesValid = validateDateFilters(state.filters ?? []);
    const mappedFilters: Record<string, unknown> = {};

    state.filters?.forEach((currentFilter) => {
      mappedFilters[`filter${currentFilter.apiaFilter.id}`] =
        currentFilter.apiaFilter.currentValue;
      if (currentFilter.apiaFilter.options) {
        currentFilter.apiaFilter.options.forEach((option) => {
          if (option.value === currentFilter.apiaFilter.currentValue) {
            mappedFilters[`filter${currentFilter.apiaFilter.id}`] =
              option.value;
          }
        });
      }
      if (['date', 'D'].includes(currentFilter.apiaFilter.type as string)) {
        mappedFilters[
          `filter${currentFilter.apiaFilter.filterToId as string}`
        ] = currentFilter.apiaFilter.filterToValue;
      }
    });

    const hasDetectOnChange =
      changingFilters?.[0]?.finderFilter?.detectOnChange;

    // Search can run while updateFilters owns the queue. In that case its
    // nested work must execute in the same queue lock; enqueueing it would
    // deadlock because updateFilters awaits this thunk before releasing it.
    const acceptedParentActions: TQueriesActionsChain[] = [
      queueKey,
      'directUpdateFilters',
      'fromSearchfromRefresh',
      'fromSearchfromUpdateFilters',
    ];

    if (hasDetectOnChange && !avoidDetectOnChangeAndSearch) {
      await queriesQueue.runAction(
        queueKey,
        async () => {
          await store.dispatch(
            loadStructure({
              fieldId,
              action: 'eventOnChange',
              query,
              method: 'POST',
              payload: {
                eventFilter: `filter${changingFilters[0].apiaFilter.id}`,
                ...mappedFilters,
              },
              queueKey:
                queueKey === 'directSearch'
                  ? 'fromSearch'
                  : queueKey === 'fromRefresh'
                    ? 'fromSearchfromRefresh'
                    : 'fromSearchfromUpdateFilters',
            }),
          );
        },
        acceptedParentActions,
      );

      await queriesQueue.runAction(
        queueKey,
        async () => {
          await ApiaApi.post(
            makeApiaUrl({
              fieldId,
              ajaxUrl: state.ajaxUrl,
              action: 'filterQuery',
              ...query,
              react: true,
            }),
            {
              postData: {
                eventFilter: `filter${changingFilters[0].apiaFilter.id}`,
                ...mappedFilters,
              },
              postDataTreatement: 'stringify',
            },
          );
        },
        acceptedParentActions,
      );
      if (forceSearch) {
        if (!areDatesValid) {
          notify({
            message: window.MSG_FEC_FIN_MAY_FEC_INI,
            type: 'warning',
          });
          return { filtersApplied: false };
        } else if (!isRequirementMet) {
          notify({
            message: formatMessage(window.LBL_FILTER_REQUIRED_LIST_QUERY, {
              TOK1: emptyFilters.join(', '),
            }),
            type: 'warning',
          });

          const newState = thunkApi.getState().querySlice.queries[fieldId];
          return {
            filters: parseReturnFilters(newState.filters),
          };
        }
        if (areDatesValid && isRequirementMet) {
          await queriesQueue.runAction(
            queueKey,
            async () => {
              await store.dispatch(
                updateData({
                  fieldId,
                  requestConfig: {
                    queryData: query,
                  },
                  queueKey:
                    queueKey === 'directSearch'
                      ? 'fromSearch'
                      : queueKey === 'fromRefresh'
                        ? 'fromSearchfromRefresh'
                        : 'fromSearchfromUpdateFilters',
                }),
              );
            },
            acceptedParentActions,
          );
        }
      } else {
        return { filtersApplied: false };
      }
    } else if (!hasDetectOnChange || avoidDetectOnChangeAndSearch) {
      if (!areDatesValid) {
        notify({
          message: window.MSG_FEC_FIN_MAY_FEC_INI,
          type: 'warning',
        });
        return { filtersApplied: areDatesValid };
      } else if (!isRequirementMet) {
        await queriesQueue.runAction(
          queueKey,
          async () => {
            await store.dispatch(
              loadStructure({
                fieldId,
                action: 'filter',
                query,
                method: 'POST',
                payload: {
                  eventFilter: '',
                  ...mappedFilters,
                },
                queueKey:
                  queueKey === 'directSearch'
                    ? 'fromSearch'
                    : queueKey === 'fromRefresh'
                      ? 'fromSearchfromRefresh'
                      : 'fromSearchfromUpdateFilters',
              }),
            );
          },
          acceptedParentActions,
        );
        if (forceSearch) {
          notify({
            message: formatMessage(window.LBL_FILTER_REQUIRED_LIST_QUERY, {
              TOK1: emptyFilters.join(', '),
            }),
            type: 'warning',
          });
          const newState = thunkApi.getState().querySlice.queries[fieldId];
          return {
            filters: parseReturnFilters(newState.filters),
          };
        }
      }
      if (isRequirementMet) {
        await queriesQueue.runAction(
          queueKey,
          async () => {
            await store.dispatch(
              loadStructure({
                fieldId,
                action: 'filter',
                query,
                method: 'POST',
                payload: {
                  eventFilter: '',
                  ...mappedFilters,
                },
                queueKey:
                  queueKey === 'directSearch'
                    ? 'fromSearch'
                    : queueKey === 'fromRefresh'
                      ? 'fromSearchfromRefresh'
                      : 'fromSearchfromUpdateFilters',
              }),
            );
          },
          acceptedParentActions,
        );

        await queriesQueue.runAction(
          queueKey,
          async () => {
            await store.dispatch(
              updateData({
                fieldId,
                requestConfig: {
                  queryData: query,
                },
                forceRefreshFromSavedFilters,
                queueKey:
                  queueKey === 'directSearch'
                    ? 'fromSearch'
                    : queueKey === 'fromRefresh'
                      ? 'fromSearchfromRefresh'
                      : 'fromSearchfromUpdateFilters',
              }),
            );
          },
          acceptedParentActions,
        );
      }
    }
    return { filtersApplied: areDatesValid };
  },
);

export default search;
